const VIDEO_MUTE_PREFERENCE_PREFIX = 'dw:video-muted:v1:'

const TRANSIENT_QUERY_PARAMS = new Set([
  'token',
  'publication_token',
  'v',
  'video_retry',
  'signature',
  'expires',
  'googleaccessid',
  'key-pair-id',
  'policy',
])

/** Remove expiring credentials/cache keys while preserving the asset identity. */
function stableVideoIdentity(videoIdentity: string): string {
  if (!videoIdentity) return videoIdentity

  try {
    const baseUrl = typeof window === 'undefined'
      ? 'https://dreamwrite.local'
      : window.location.href
    const parsed = new URL(videoIdentity, baseUrl)
    for (const name of Array.from(parsed.searchParams.keys())) {
      const normalizedName = name.toLowerCase()
      if (
        TRANSIENT_QUERY_PARAMS.has(normalizedName) ||
        normalizedName.startsWith('x-goog-') ||
        normalizedName.startsWith('x-amz-')
      ) {
        parsed.searchParams.delete(name)
      }
    }
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return videoIdentity
  }
}

/** Build a namespaced localStorage key for one normalized video identity. */
function storageKey(videoIdentity: string): string {
  return `${VIDEO_MUTE_PREFERENCE_PREFIX}${encodeURIComponent(stableVideoIdentity(videoIdentity))}`
}

/**
 * Return the browser-local mute choice for one video. Videos without a saved
 * choice intentionally default to sound on.
 */
export function getVideoMutedPreference(videoIdentity: string): boolean {
  if (typeof window === 'undefined' || !videoIdentity) return false
  try {
    return window.localStorage.getItem(storageKey(videoIdentity)) === 'true'
  } catch {
    return false
  }
}

/** Persist a per-video preference in this browser only; canvas/DB state is untouched. */
export function setVideoMutedPreference(
  videoIdentity: string,
  muted: boolean,
): void {
  if (typeof window === 'undefined' || !videoIdentity) return
  try {
    window.localStorage.setItem(storageKey(videoIdentity), String(muted))
  } catch {
    // Storage can be unavailable in restricted/private browser contexts.
  }
}
